// client/src/hooks/useHomeConfig.js
// client/src/hooks/useHomeConfig.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useAlert from './useAlert';

/**
 * Hook para GERENCIAR a configuração da Home no PAINEL DE ADMINISTRAÇÃO.
 * Utiliza as rotas de admin.
 */
export const useHomeConfig = () => {
    const [homeConfig, setHomeConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setAlert } = useAlert();

    const fetchHomeConfig = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/institucional/home-config');
            setHomeConfig(response.data.data);
            setError(null);
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao buscar configuração da home';
            setError(msg);
            setAlert(msg, 'error');
            setHomeConfig(null);
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const updateHomeConfig = useCallback(async (configData) => {
        try {
            setLoading(true);
            const response = await api.put('/admin/institucional/home-config', configData);
            setHomeConfig(response.data.data);
            setAlert('Configurações da Home salvas com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao atualizar configuração da home';
            setAlert(msg, 'error');
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const resetHomeConfig = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.post('/admin/institucional/home-config/reset');
            setHomeConfig(response.data.data);
            setAlert('Configurações da Home resetadas com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao resetar configuração da home';
            setAlert(msg, 'error');
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    // ✅ FUNÇÕES PARA GERENCIAMENTO DE BANNERS NAS SEÇÕES
    const addBannerToSection = useCallback(async (sectionIndex, bannerId) => {
        try {
            setLoading(true);
            const response = await api.post(`/admin/institucional/home-config/sections/${sectionIndex}/banners`, {
                bannerId
            });
            
            // ✅ CORREÇÃO: Recarrega a configuração completa para garantir sincronização
            await fetchHomeConfig();
            
            setAlert('Banner adicionado à seção com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao adicionar banner à seção';
            setAlert(msg, 'error');
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [fetchHomeConfig, setAlert]);

    const removeBannerFromSection = useCallback(async (sectionIndex, bannerId) => {
        try {
            setLoading(true);
            const response = await api.delete(`/admin/institucional/home-config/sections/${sectionIndex}/banners/${bannerId}`);
            
            // ✅ CORREÇÃO: Recarrega a configuração completa para garantir sincronização
            await fetchHomeConfig();
            
            setAlert('Banner removido da seção com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao remover banner da seção';
            setAlert(msg, 'error');
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [fetchHomeConfig, setAlert]);

    const updateSectionBanners = useCallback(async (sectionIndex, bannerIds) => {
        try {
            setLoading(true);
            const response = await api.put(`/admin/institucional/home-config/sections/${sectionIndex}/banners`, {
                bannerIds
            });
            
            // ✅ CORREÇÃO: Recarrega a configuração completa para garantir sincronização
            await fetchHomeConfig();
            
            setAlert('Banners da seção atualizados com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao atualizar banners da seção';
            setAlert(msg, 'error');
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, [fetchHomeConfig, setAlert]);

    // ✅ FUNÇÃO PARA REORDENAR BANNERS EM UMA SEÇÃO
    const reorderSectionBanners = useCallback(async (sectionIndex, startIndex, endIndex) => {
        if (!homeConfig) return;

        const section = homeConfig.sections[sectionIndex];
        if (!section || !section.config || !section.config.bannerIds) return;

        const updatedBannerIds = [...section.config.bannerIds];
        const [movedBanner] = updatedBannerIds.splice(startIndex, 1);
        updatedBannerIds.splice(endIndex, 0, movedBanner);

        try {
            await updateSectionBanners(sectionIndex, updatedBannerIds);
        } catch (error) {
            setAlert('Erro ao reordenar banners: ' + error.message, 'error');
            throw error;
        }
    }, [homeConfig, updateSectionBanners, setAlert]);

    // ✅ FUNÇÃO PARA OBTER SEÇÕES DO TIPO BANNER
    const getBannerSections = useCallback(() => {
        if (!homeConfig || !homeConfig.sections) return [];
        
        return homeConfig.sections
            .map((section, index) => ({ ...section, index }))
            .filter(section => section.type === 'banner');
    }, [homeConfig]);

    // ✅ FUNÇÃO PARA OBTER BANNERS DE UMA SEÇÃO ESPECÍFICA
    const getBannersFromSection = useCallback((sectionIndex) => {
        if (!homeConfig || !homeConfig.sections || sectionIndex >= homeConfig.sections.length) {
            return [];
        }
        
        const section = homeConfig.sections[sectionIndex];
        if (section.type === 'banner' && section.config && section.config.bannerIds) {
            return section.config.bannerIds;
        }
        
        return [];
    }, [homeConfig]);

    useEffect(() => {
        fetchHomeConfig();
    }, [fetchHomeConfig]);

    return {
        homeConfig,
        loading,
        error,
        
        // Funções principais
        updateHomeConfig,
        resetHomeConfig,
        refetch: fetchHomeConfig,
        
        // ✅ NOVAS FUNÇÕES PARA BANNERS
        addBannerToSection,
        removeBannerFromSection,
        updateSectionBanners,
        reorderSectionBanners,
        
        // ✅ FUNÇÕES UTILITÁRIAS
        getBannerSections,
        getBannersFromSection
    };
};